// ── Welle D3c: Hinweis-Leiste über dem Board ──
// Pflicht-Schritte und Mitteilungen (nicht ausblendbar über das Board): Klärung, Übergang,
// Mitteilung des Betreibers, Datenschutz-Fassung. Einheitlicher Look, je ein Handlungsknopf.
import React, { useEffect, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiPost } from '../../../lib/api';
import { globalNav, oeffneBeitrag } from '../../../lib/nav';
import { uebergangApi } from '../../../lib/uebergang-api';
import { VORLAGE_ICON } from '../../sicherheit/typen';
import type { Uebersicht } from '../../sicherheit/typen';
import { ladeNeu, useLader } from './lader';

type Props = {
  icon: string;
  ton?: 'violett' | 'amber';
  titel: string;
  text?: string | null;
  aktion: { label: string; onClick: () => void };
  neben?: { label: string; onClick: () => void };
  onSchliessen?: () => void;
  schliessenLabel?: string;
};

export function Hinweis({ icon, ton = 'violett', titel, text, aktion, neben, onSchliessen, schliessenLabel = 'Hinweis ausblenden' }: Props) {
  return (
    <div className={`ld-hinweis ld-hinweis--${ton}`} role="status">
      <span className="ld-hinweis__icon"><MIcon name={icon} size={18} /></span>
      <div className="ld-hinweis__text">
        <b>{titel}</b>
        {text && <span>{text}</span>}
      </div>
      <div className="ld-hinweis__aktionen">
        {neben && <button type="button" className="ld-hinweis__neben" onClick={neben.onClick}>{neben.label}</button>}
        <button type="button" className="ld-hinweis__knopf" onClick={aktion.onClick}>{aktion.label}</button>
        {onSchliessen && (
          <button type="button" className="ld-hinweis__zu" aria-label={schliessenLabel} onClick={onSchliessen}><MIcon name="close" size={17} /></button>
        )}
      </div>
    </div>
  );
}

type KlaerungItem = { id: number; titel: string; beschreibung?: string | null };

/** Klärung + Übergang + Beitrag aus „Sicherheit & Tipps“ (Datenschutz und Note-6 rendert HeuteScreen selbst) */
export function StandardHinweise() {
  const kl = useLader<{ items: KlaerungItem[]; gesamt: number }>('/api/klaerung/items', 60_000);
  const [uebergang, setUebergang] = useState<{ sj: string; tage: number | null } | null>(null);
  // Welle A3: ein Beitrag mit „Als Hinweis auf Heute“, bis gelesen oder ausgeblendet
  const bt = useLader<Uebersicht>('/api/beitraege');
  const [weg, setWeg] = useState<number | null>(null);
  const beitrag = bt.daten?.hinweis && bt.daten.hinweis.id !== weg ? bt.daten.hinweis : null;
  useEffect(() => {
    uebergangApi.status()
      .then(s => { if (s.trigger_status === 'soft') setUebergang({ sj: s.naechstes_schuljahr, tage: s.tage_bis_hard_cutoff ?? null }); })
      .catch(() => {});
  }, []);

  const kGesamt = kl.daten?.gesamt ?? 0;
  return (
    <>
      {kGesamt > 0 && (
        <Hinweis icon="help" ton="amber"
          titel={`Klärung benötigt (${kGesamt})`}
          text={kl.daten!.items[0]?.titel ?? null}
          aktion={{ label: 'Jetzt klären', onClick: () => globalNav('klaerung') }} />
      )}
      {uebergang && (
        <Hinweis icon="event_repeat"
          titel={`Bereit für ${uebergang.sj}?`}
          text={uebergang.tage != null && uebergang.tage > 0 ? `Noch ${uebergang.tage} Tage, dann ist der Schuljahreswechsel Pflicht.` : 'Klassen für das neue Schuljahr vorbereiten.'}
          aktion={{ label: 'Übergang starten', onClick: () => globalNav('uebergang') }} />
      )}
      {beitrag && (
        <Hinweis icon={VORLAGE_ICON[beitrag.vorlage] ?? 'shield'} ton={beitrag.vorlage === 'warnung' ? 'amber' : 'violett'}
          titel={beitrag.titel}
          text={beitrag.teaser}
          aktion={{ label: 'Lesen', onClick: () => oeffneBeitrag(beitrag.id) }}
          schliessenLabel="Hinweis ausblenden"
          onSchliessen={() => {
            setWeg(beitrag.id);
            apiPost(`/api/beitraege/${beitrag.id}/ausblenden`).then(() => ladeNeu('/api/beitraege'))
              .catch(e => console.warn('[api] Hinweis ausblenden fehlgeschlagen:', e));
          }} />
      )}
    </>
  );
}
